import React, { useState, useEffect } from "react";
import AssignUsers from "./AssignUsers";
import UploadFiles from "./UploadFiles";
import ViewBrainBankFiles from "./ViewBrainBankFiles";
import DepartmentSelector from "../HatManagementComponents/DepartmentSelector";
import { updateKnowledgeBase } from "../../../../service/apiService";
import { toast } from "sonner";
import { SaveIcon, LoaderCircle } from "lucide-react";

// Define BrainBank type
interface BrainBank {
  id: string;
  knowledgebase_name: string;
  index_name: string;
  description: string;
  department?: string;
  file_ids: string[];
  last_sync: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  summary: string | null;
  files?: Array<{
    id: string;
    file_name: string;
    status: string;
    created_by: string;
    created_at: string;}>;
}

interface EditBrainBankProps {
  brainBank: BrainBank;
  onRefresh: () => void;
}

const EditBrainBank: React.FC<EditBrainBankProps> = ({ brainBank, onRefresh }) => {
  const [activeTab, setActiveTab] = useState<"upload" | "manage" | "assign">("upload");
  
  // Only keep department state since name and description are no longer editable
  const [currentDepartment, setCurrentDepartment] = useState<string>("");
  
  // UI state
  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize department state from brainBank prop
  useEffect(() => {
    setCurrentDepartment(brainBank.department || "");
  }, [brainBank]);

  // Department handlers
  const handleDepartmentSelected = (departmentName: string) => {
    console.log(`Department selected: "${departmentName}"`);
    const trimmedName = departmentName.trim();
    setCurrentDepartment(trimmedName);
  };

  const handleDepartmentRemoved = () => {
    console.log("Department removed");
    setCurrentDepartment("");
  };

  const handleUpdateBrainBank = async () => {
    setIsUpdating(true);
    try {
      const updatePayload = {
        department: currentDepartment.trim() === "" ? "" : currentDepartment.trim() 
      };

      await updateKnowledgeBase(brainBank.id, updatePayload);
      
      toast.success("Department updated successfully!");
      onRefresh();
    } catch (error) {
      console.error("Error updating BrainBank:", error);
      toast.error("Failed to update department");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-6 p-4">
      {/* BrainBank Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-1 gap-4">
          {/* BrainBank Name */}
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              BrainBank Name
            </label>
            <div className="w-full px-4 py-3 text-sm text-gray-900 bg-gray-100 border border-gray-200 rounded-lg cursor-not-allowed">
              {brainBank.knowledgebase_name}
            </div>
          </div>

          {/* Description */}
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <div className="w-full px-4 py-3 text-sm text-gray-900 bg-gray-100 border border-gray-200 rounded-lg min-h-[80px] cursor-not-allowed">
              {brainBank.description}
            </div>
          </div>

          {/* Index Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Index Name (Auto-generated)
              </label>
              <div className="w-full px-4 py-3 text-sm text-gray-600 bg-gray-100 border border-gray-200 rounded-lg font-mono">
                {brainBank.index_name}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Department Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Department Assignment
          </h3>
          <div className="border border-gray-200 rounded-md p-4 h-48 overflow-y-auto">
            <DepartmentSelector
              selectedDepartmentName={currentDepartment}
              onDepartmentSelected={handleDepartmentSelected}
              onDepartmentRemoved={handleDepartmentRemoved}
              isEnabled={true}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            You can assign this BrainBank to a department or leave it unassigned.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab("upload")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "upload"
                ? "border-violet-500 text-violet-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Upload Files
          </button>
          <button
            onClick={() => setActiveTab("manage")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "manage"
                ? "border-violet-500 text-violet-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Manage Files
          </button>
          <button
            onClick={() => setActiveTab("assign")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "assign"
                ? "border-violet-500 text-violet-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Assign Users
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === "upload" && (
          <UploadFiles 
            refresh={0}
            preSelectedKnowledgeBase={{
              id: brainBank.id,
              name: brainBank.knowledgebase_name,
              index_name: brainBank.index_name
            }}
          />
        )}
        {activeTab === "manage" && (
          <ViewBrainBankFiles brainBank={brainBank} onRefresh={onRefresh} />
        )}
        {activeTab === "assign" && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Note:</strong> You can assign any BrainBank to users from this section. The current BrainBank is "{brainBank.knowledgebase_name}".
                  </p>
                </div>
              </div>
            </div>
            <AssignUsers 
              currentBrainBank={{
                id: brainBank.id,
                name: brainBank.knowledgebase_name
              }}/>
          </div>
        )}
      </div>

      {/* Update Button */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleUpdateBrainBank}
            disabled={isUpdating}
            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <>
                <LoaderCircle className="animate-spin -ml-1 mr-2 h-5 w-5" />
                Updating...
              </>
            ) : (
              <>
                <SaveIcon className="-ml-1 mr-2 h-5 w-5" />
                Update Brainbank
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditBrainBank;